import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { StoreProvider, useAuthStore } from "./stores/StoreContext";
import Header from "./components/Header";
import ScrollToTop from "./components/ScrollToTop";
import PageLoader from "./components/PageLoader";

const Home              = lazy(() => import("./pages/Home"));
const SignUp            = lazy(() => import("./pages/SignUp"));
const Login             = lazy(() => import("./pages/Login"));
const PostRoom          = lazy(() => import("./pages/PostRoom"));
const SearchRooms       = lazy(() => import("./pages/SearchRooms"));
const Lookout           = lazy(() => import("./pages/Lookout"));
const Profile           = lazy(() => import("./pages/Profile"));
const ProfileActivity   = lazy(() => import("./pages/ProfileActivity"));
const SubmitRequirement = lazy(() => import("./pages/SubmitRequirement"));
const Requirements      = lazy(() => import("./pages/Requirements"));
const TravelCompanion   = lazy(() => import("./pages/TravelCompanion"));
const RoomDetail        = lazy(() => import("./pages/RoomDetail"));
const RequirementDetail = lazy(() => import("./pages/RequirementDetail"));
const TravelDetail      = lazy(() => import("./pages/TravelDetail"));

const CURRENT_YEAR = new Date().getFullYear();

const AppContent = observer(() => {
  const authStore = useAuthStore();

  if (authStore.loading) {
    return <PageLoader message="Loading..." fullScreen />;
  }

  return (
    <div className="viewport">
      <ScrollToTop />
      <Header />

      <main className="site-main">
        <Suspense fallback={<PageLoader fullScreen />}>
          <Routes>
            {/* Public routes — no auth required to view */}
            <Route path="/"                    element={<Home />} />
            <Route path="/signup"              element={<SignUp />} />
            <Route path="/login"               element={<Login />} />
            <Route path="/search"              element={<SearchRooms />} />
            <Route path="/rooms/:id"           element={<RoomDetail />} />
            <Route path="/requirements"        element={<Requirements />} />
            <Route path="/requirements/:id"    element={<RequirementDetail />} />
            <Route path="/travel-companion"    element={<TravelCompanion />} />
            <Route path="/travel/:id"          element={<TravelDetail />} />

            {/* Auth-required routes */}
            <Route path="/post-room"           element={<PostRoom />} />
            <Route path="/lookout"             element={<Lookout />} />
            <Route path="/profile"             element={<Profile />} />
            <Route path="/activity"            element={<ProfileActivity />} />
            <Route path="/submit-requirement"  element={<SubmitRequirement />} />
          </Routes>
        </Suspense>
      </main>

      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4 className="footer-heading">Contact</h4>
            <div className="footer-contact">
              <a href="mailto:build.and.render1998@gmail.com">
                contact@webtrabs.com
              </a>
            </div>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">About</h4>
            <p className="footer-tagline">Where Every Journey Finds a Companion</p>
            <p className="footer-about">
              TROCYO helps travelers find rooms and connects people looking for
              accommodations with those who have space to share.
            </p>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Browse</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <a href="/search" style={{ fontSize: 14, color: "var(--color-text-muted)" }}>Search Rooms</a>
              <a href="/requirements" style={{ fontSize: 14, color: "var(--color-text-muted)" }}>View Requirements</a>
              <a href="/travel-companion" style={{ fontSize: 14, color: "var(--color-text-muted)" }}>Travel Companions</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <small className="copyright">© {CURRENT_YEAR} WebTrabs. All rights reserved.</small>
        </div>
      </footer>
    </div>
  );
});

export default function App() {
  return (
    <BrowserRouter>
      <StoreProvider>
          <AppContent />
      </StoreProvider>
    </BrowserRouter>
  );
}
